// src/app/context/SnackbarContext.tsx
import React, { createContext, useContext, useState, useCallback } from 'react';
import CustomSnackbar, { ToastType } from '../components/CustomSnackbar';
import { SnackbarService } from '../../app/services/SnackbarService';

interface SnackbarContextType {
  showSnackbar: (message: string, type?: ToastType, duration?: number) => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const SnackbarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [visible, setVisible] = useState(false);
  const [message, setMessage] = useState('');
  const [type, setType] = useState<ToastType>('primary');
  const [duration, setDuration] = useState(3000);

  const showSnackbar = useCallback((msg: string, tp: ToastType = 'primary', dur = 3000) => {
    setMessage(msg);
    setType(tp);
    setDuration(dur);
    setVisible(true);
  }, []);

  React.useEffect(() => {
    SnackbarService.register(showSnackbar);
  }, [showSnackbar]);

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <CustomSnackbar
        message={message}
        type={type}
        show={visible}
        duration={duration}
        onClose={() => setVisible(false)}
      />
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = (): SnackbarContextType => {
  const context = useContext(SnackbarContext);
  if (!context) throw new Error('useSnackbar must be used within SnackbarProvider');
  return context;
};
